// فرم «کد تأیید + رمز تازه» مشترک پذیرش دعوت (AC-IAM-012.1)، تعیین رمز مالک (AC-IAM-010.1) و بازیابی رمز (AC-IAM-004.1).
// خطاها: PHX-IAM-0301 ← فیلد کد (با تلاش‌های باقی‌مانده)، PHX-IAM-0303 ← فیلد رمز؛ بقیه با ErrorState.
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { isApiError } from '@phoenix/api-client';
import { errorMessageKey, useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, TextFieldRHF } from '@phoenix/ui';
import type { FormEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { newPasswordSchema, OTP_LENGTH, otpCodeSchema, PasswordRules } from './password';

export interface OtpPasswordValues {
  code: string;
  password: string;
}

function schema(mobile?: string) {
  return z.intersection(z.object({ code: otpCodeSchema }), newPasswordSchema(mobile));
}

export function OtpPasswordForm({
  submitKey,
  mobile,
  onSubmit,
  onResend,
  resendBusy,
  busy,
  error,
}: {
  submitKey: string;
  /** موبایل خام (فقط در بازیابی، برای قاعده «شامل موبایل نباشد»). */
  mobile?: string;
  onSubmit: (values: OtpPasswordValues) => void;
  onResend: () => void;
  resendBusy?: boolean;
  busy: boolean;
  error: unknown;
}) {
  const { t } = useTranslation('iam');
  const resolved = schema(mobile);
  const { control, handleSubmit } = useForm<z.input<typeof resolved>>({
    resolver: zodResolver(resolved),
    defaultValues: { code: '', password: '', confirm: '' },
  });
  const password = useWatch({ control, name: 'password' });

  // خطاهای سرور روی فیلد مربوط (کد یا رمز)؛ بقیه عمومی.
  const code = isApiError(error) ? error.code : undefined;
  const remaining = isApiError(error) ? error.extensions['remainingAttempts'] : undefined;
  const codeError =
    code === 'PHX-IAM-0301'
      ? `${t(errorMessageKey(code))}${
          typeof remaining === 'number' && remaining > 0
            ? ` ${t('iam:otp.remaining', { value: faDigits(remaining) })}`
            : ''
        }`
      : undefined;
  const passwordError = code === 'PHX-IAM-0303' ? t(errorMessageKey(code)) : undefined;
  const generalError = error && !codeError && !passwordError ? error : undefined;

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        void handleSubmit((values) => onSubmit({ code: values.code, password: values.password }))();
      }}
    >
      <Stack spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="iam:otp.code"
          helperKey="iam:otp.code_hint"
          required
          autoFocus
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              dir: 'ltr',
              maxLength: OTP_LENGTH + 2,
              autoComplete: 'one-time-code',
              'data-testid': 'otp-code',
            },
          }}
        />
        {codeError && (
          <Box role="alert" sx={{ color: 'error.main', fontSize: 14 }} data-testid="otp-error">
            {codeError}
          </Box>
        )}
        <TextFieldRHF
          control={control}
          name="password"
          labelKey="iam:password.new"
          type="password"
          required
          slotProps={{
            htmlInput: { dir: 'ltr', autoComplete: 'new-password', 'data-testid': 'new-password' },
          }}
        />
        <PasswordRules password={password} mobile={mobile} />
        {passwordError && (
          <Box role="alert" sx={{ color: 'error.main', fontSize: 14 }} data-testid="password-error">
            {passwordError}
          </Box>
        )}
        <TextFieldRHF
          control={control}
          name="confirm"
          labelKey="iam:password.confirm"
          type="password"
          required
          slotProps={{
            htmlInput: { dir: 'ltr', autoComplete: 'new-password', 'data-testid': 'confirm-password' },
          }}
        />
        {generalError ? <ErrorState error={generalError} compact /> : null}
        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="contained" disabled={busy} data-testid="otp-password-submit">
            {t(submitKey)}
          </Button>
          <Button onClick={onResend} disabled={busy || resendBusy} data-testid="otp-resend">
            {t('iam:otp.resend')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
